import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";

import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { selectActiveCompanyId } from "../../redux/features/user/selectors/userState.selectors";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { colors } from "../ui/colors";
import { brandButton } from "../ui/styles/buttonStyles";
import { useCreateEventDeliveryConfig, useDeleteEventDeliveryConfig, useUpdateEventDeliveryConfig } from "./hooks/useEventDeliveryConfigMutation";
import { useGetEventDeliveryConfig } from "./hooks/useGetEventDeliveryConfig";
import {
  useRevealEventDeliveryApiKey,
  useRevealEventDeliverySigningSecret,
  useRollEventDeliverySigningSecret,
} from "./hooks/useEventDeliverySecrets";

const apiKeyStorageKey = (companyId: number | string) => `eventDeliveryApiKey:${companyId}`;
const signingSecretStorageKey = (companyId: number | string) => `eventDeliverySigningSecret:${companyId}`;

const getErrorMessage = (error: any) =>
  error?.message || error?.response?.data?.message || error?.data?.message || "Could not update event delivery configuration.";

export interface EventDeliveryConfigFormProps {
  compact?: boolean;
}

export const EventDeliveryConfigForm = ({ compact = false }: EventDeliveryConfigFormProps) => {
  const companyId = useSelector(selectActiveCompanyId);
  const { showSnackbar } = useGlobalSnackbar();
  const { data: config, isLoading } = useGetEventDeliveryConfig();
  const createConfig = useCreateEventDeliveryConfig();
  const updateConfig = useUpdateEventDeliveryConfig();
  const deleteConfig = useDeleteEventDeliveryConfig();
  const revealApiKey = useRevealEventDeliveryApiKey();
  const revealSigningSecret = useRevealEventDeliverySigningSecret();
  const rollSigningSecret = useRollEventDeliverySigningSecret();

  const [webhookUrl, setWebhookUrl] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showSigningSecret, setShowSigningSecret] = useState(false);
  const [signingSecret, setSigningSecret] = useState("");
  const [previousSecretValidUntil, setPreviousSecretValidUntil] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmRollSecret, setConfirmRollSecret] = useState(false);

  const isRegistered = Boolean(config?.uid);
  const isSaving = createConfig.isPending || updateConfig.isPending;
  const isRemoving = deleteConfig.isPending;

  useEffect(() => {
    if (!config) return;
    setWebhookUrl(config.webhookUrl || "");
    setIsEnabled(Boolean(config.isEnabled));
    setAuthToken("");
  }, [config]);

  useEffect(() => {
    if (!companyId) return;
    const storedKey = sessionStorage.getItem(apiKeyStorageKey(companyId));
    if (storedKey) setApiKey(storedKey);
    const storedSecret = sessionStorage.getItem(signingSecretStorageKey(companyId));
    if (storedSecret) setSigningSecret(storedSecret);
  }, [companyId]);

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showSnackbar(`${label} copied to clipboard`, "success");
      if (label.toLowerCase().includes("url")) {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      }
    } catch {
      showSnackbar(`Could not copy ${label.toLowerCase()}`, "error");
    }
  };

  const handleSave = async () => {
    const trimmedUrl = webhookUrl.trim();
    if (!trimmedUrl) {
      showSnackbar("Webhook URL is required", "warning");
      return;
    }

    try {
      if (isRegistered) {
        await updateConfig.mutateAsync({
          webhookUrl: trimmedUrl,
          isEnabled,
          ...(authToken.trim() ? { authToken: authToken.trim() } : {}),
        });
        showSnackbar("Event delivery configuration updated successfully", "success");
        setShowEditForm(false);
        return;
      }

      const created = await createConfig.mutateAsync({
        webhookUrl: trimmedUrl,
        isEnabled,
      });

      const createdKey = created?.apiKey?.key;
      if (createdKey && companyId) {
        setApiKey(createdKey);
        sessionStorage.setItem(apiKeyStorageKey(companyId), createdKey);
      }

      const createdSecret = created?.signingSecret;
      if (createdSecret && companyId) {
        setSigningSecret(createdSecret);
        sessionStorage.setItem(signingSecretStorageKey(companyId), createdSecret);
      }

      showSnackbar("Event delivery configuration created", "success");
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    }
  };

  const handleRemove = async () => {
    try {
      await deleteConfig.mutateAsync();
      if (companyId) {
        sessionStorage.removeItem(apiKeyStorageKey(companyId));
        sessionStorage.removeItem(signingSecretStorageKey(companyId));
      }
      setApiKey("");
      setSigningSecret("");
      setPreviousSecretValidUntil(null);
      setWebhookUrl("");
      setAuthToken("");
      setIsEnabled(true);
      setShowEditForm(false);
      showSnackbar("Webhook settings removed", "success");
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    }
  };

  const handleRevealApiKey = async () => {
    try {
      const data = await revealApiKey.mutateAsync();
      if (data?.key) {
        setApiKey(data.key);
        setShowApiKey(true);
        if (companyId) sessionStorage.setItem(apiKeyStorageKey(companyId), data.key);
        showSnackbar("API key revealed", "success");
      }
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    }
  };

  const handleRevealSigningSecret = async () => {
    try {
      const data = await revealSigningSecret.mutateAsync();
      if (data?.signingSecret) {
        setSigningSecret(data.signingSecret);
        setShowSigningSecret(true);
        if (companyId) sessionStorage.setItem(signingSecretStorageKey(companyId), data.signingSecret);
        showSnackbar("Signing secret revealed", "success");
      }
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    }
  };

  const handleRollSecret = async () => {
    try {
      const data = await rollSigningSecret.mutateAsync();
      if (data?.signingSecret) {
        setSigningSecret(data.signingSecret);
        setShowSigningSecret(true);
        setPreviousSecretValidUntil(data.previousSecretValidUntil || null);
        if (companyId) sessionStorage.setItem(signingSecretStorageKey(companyId), data.signingSecret);
        setConfirmRollSecret(false);
        showSnackbar("Signing secret rolled successfully. Previous secret remains valid for 24h.", "success");
      }
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    }
  };

  const handleToggleEnable = async (checked: boolean) => {
    setIsEnabled(checked);
    if (isRegistered && config) {
      try {
        await updateConfig.mutateAsync({
          webhookUrl: config.webhookUrl,
          isEnabled: checked,
        });
        showSnackbar(`Event delivery ${checked ? "enabled" : "paused"}`, "success");
      } catch (error) {
        setIsEnabled(!checked);
        showSnackbar(getErrorMessage(error), "error");
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320 }}>
        <CircularProgress size={32} sx={{ color: colors.orange.base }} />
      </Box>
    );
  }

  // COMPACT MODE (Sidebar / Registered View)
  if (compact) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Header & Status Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            background: `linear-gradient(135deg, ${colors.orange[50]} 0%, var(--fx-surface-base, #FFFFFF) 100%)`,
            border: `1px solid ${colors.orange[100]}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: `linear-gradient(135deg, ${colors.orange.base}, ${colors.orange[600]})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                boxShadow: "0 4px 12px rgba(251, 142, 0, 0.2)",
              }}
            >
              <NotificationsActiveOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", lineHeight: 1.2 }}>
                Event Delivery
              </Typography>
              <Typography sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontSize: 12, fontWeight: 500, mt: 0.25 }}>
                Webhook configuration
              </Typography>
            </Box>
          </Box>

          <Chip
            size="small"
            label={isEnabled ? "Active" : "Paused"}
            color={isEnabled ? "success" : "warning"}
            sx={{ fontWeight: 700, fontSize: 11, borderRadius: "999px" }}
          />
        </Paper>

        {/* Endpoint Preview Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
            boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--fx-surface-textsecondary, #64748B)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Webhook Endpoint
            </Typography>
            <Tooltip title={copiedUrl ? "Copied!" : "Copy Webhook URL"}>
              <IconButton size="small" onClick={() => handleCopy(webhookUrl, "Webhook URL")} sx={{ p: 0.5 }}>
                {copiedUrl ? <CheckOutlinedIcon sx={{ fontSize: 15, color: "#16a34a" }} /> : <ContentCopyOutlined sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 12,
              fontFamily: "monospace, monospace",
              color: "var(--fx-surface-text, #0F172A)",
              wordBreak: "break-all",
              lineHeight: 1.4,
              bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
              p: 1,
              borderRadius: "8px",
              border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.05)",
            }}
          >
            {config?.webhookUrl || webhookUrl || "No URL configured"}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 0.25 }}>
            {config?.hasSigningSecret ? (
              <Chip
                size="small"
                icon={<SecurityOutlinedIcon style={{ fontSize: 12 }} />}
                label="HMAC Signed"
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: "6px",
                  bgcolor: "rgba(16, 185, 129, 0.1)",
                  color: "#059669",
                }}
              />
            ) : null}
            {config?.hasAuthToken ? (
              <Chip
                size="small"
                icon={<KeyOutlinedIcon style={{ fontSize: 12 }} />}
                label="Bearer Token Set"
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: "6px",
                  bgcolor: "rgba(59, 130, 246, 0.08)",
                  color: "#2563eb",
                }}
              />
            ) : null}
            {!config?.hasSigningSecret && !config?.hasAuthToken ? (
              <Chip
                size="small"
                label="No Secret Configured"
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: "6px",
                  bgcolor: "rgba(100, 116, 139, 0.08)",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                }}
              />
            ) : null}
          </Box>
        </Paper>

        {/* Quick Toggle Delivery Switch */}
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: "16px",
            bgcolor: isEnabled ? "rgba(34, 197, 94, 0.03)" : "rgba(245, 158, 11, 0.03)",
            border: isEnabled ? "1px solid rgba(34, 197, 94, 0.2)" : "1px solid rgba(245, 158, 11, 0.2)",
            transition: "all 0.2s ease",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={(event) => handleToggleEnable(event.target.checked)}
                color="warning"
                size="small"
                disabled={isSaving}
              />
            }
            label={
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: colors.gray.base, ml: 0.5 }}>
                {isEnabled ? "Notifications Enabled" : "Notifications Paused"}
              </Typography>
            }
            sx={{ ml: 0, mr: 0 }}
          />
        </Paper>

        {/* Collapsible Edit Settings */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
            boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
          }}
        >
          <Button
            fullWidth
            onClick={() => setShowEditForm(!showEditForm)}
            sx={{
              display: "flex",
              alignItems: "center",
              justify: "space-between",
              textTransform: "none",
              color: colors.gray.base,
              fontWeight: 700,
              fontSize: 13,
              p: 0,
              "&:hover": { bgcolor: "transparent", color: colors.orange.base },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditOutlinedIcon sx={{ fontSize: 16, color: colors.orange.base }} />
              Edit Endpoint Settings
            </Box>
            <ExpandMoreOutlinedIcon
              sx={{
                transform: showEditForm ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
                fontSize: 18,
              }}
            />
          </Button>

          <Collapse in={showEditForm} timeout="auto" unmountOnExit>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                pt: 2,
                mt: 1,
                borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
              }}
            >
              <TextField
                label="Webhook URL"
                placeholder="https://api.yourcompany.com/webhook"
                value={webhookUrl}
                onChange={(event) => setWebhookUrl(event.target.value)}
                fullWidth
                size="small"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkOutlinedIcon sx={{ fontSize: 16, color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "10px", fontSize: 12 },
                }}
              />

              <TextField
                label="Auth Token"
                placeholder={config?.hasAuthToken ? "Leave blank to keep current" : "Optional secret"}
                type={showAuthToken ? "text" : "password"}
                value={authToken}
                onChange={(event) => setAuthToken(event.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyOutlinedIcon sx={{ fontSize: 16, color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowAuthToken((current) => !current)} edge="end" size="small">
                        {showAuthToken ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "10px", fontSize: 12 },
                }}
              />

              <Button
                sx={{
                  ...brandButton,
                  width: "100%",
                  minWidth: "unset",
                  height: "38px",
                  fontSize: 13,
                }}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <>
                    <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                    Save Settings
                  </>
                )}
              </Button>
            </Box>
          </Collapse>
        </Paper>

        {/* Signing Secret Card (If exists or can be revealed) */}
        {signingSecret ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "16px",
              bgcolor: "rgba(16, 185, 129, 0.06)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SecurityOutlinedIcon sx={{ color: "#059669", fontSize: 18 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#065f46" }}>Signing Secret</Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  rollSigningSecret.isPending ? <CircularProgress size={14} color="inherit" /> : <AutorenewOutlinedIcon sx={{ fontSize: 14 }} />
                }
                onClick={() => setConfirmRollSecret(true)}
                disabled={rollSigningSecret.isPending}
                sx={{
                  textTransform: "none",
                  fontSize: 11,
                  fontWeight: 700,
                  height: 26,
                  px: 1,
                  borderRadius: "8px",
                  borderColor: "rgba(16, 185, 129, 0.4)",
                  color: "#065f46",
                  "&:hover": { borderColor: "#059669", bgcolor: "rgba(16, 185, 129, 0.1)" },
                }}
              >
                Roll Secret
              </Button>
            </Box>

            {previousSecretValidUntil ? (
              <Typography sx={{ fontSize: 11, color: "#047857", fontWeight: 600 }}>
                Previous secret valid until {new Date(previousSecretValidUntil).toLocaleString()}.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 11, color: "#047857", lineHeight: 1.4 }}>Verify webhook payloads with HMAC-SHA256 signature.</Typography>
            )}

            <TextField
              value={signingSecret}
              type={showSigningSecret ? "text" : "password"}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", bgcolor: "var(--fx-surface-base, #FFFFFF)", borderRadius: "8px", fontSize: 11 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowSigningSecret((c) => !c)} size="small">
                      {showSigningSecret ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
                    </IconButton>
                    <IconButton onClick={() => handleCopy(signingSecret, "Signing Secret")} size="small">
                      <ContentCopyOutlined fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        ) : isRegistered && config?.hasSigningSecret ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "16px",
              bgcolor: "rgba(16, 185, 129, 0.05)",
              border: "1px solid rgba(16, 185, 129, 0.25)",
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SecurityOutlinedIcon sx={{ color: "#059669", fontSize: 18 }} />
              <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#065f46" }}>Webhook Signing Secret</Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: "#047857" }}>HMAC signature verification is active for your webhook.</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={revealSigningSecret.isPending ? <CircularProgress size={14} color="inherit" /> : <VisibilityOutlined fontSize="small" />}
                onClick={handleRevealSigningSecret}
                disabled={revealSigningSecret.isPending}
                sx={{
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 700,
                  height: 30,
                  borderRadius: "8px",
                  borderColor: "rgba(16, 185, 129, 0.5)",
                  color: "#065f46",
                  "&:hover": { borderColor: "#059669", bgcolor: "rgba(16, 185, 129, 0.1)" },
                }}
              >
                Reveal Secret
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  rollSigningSecret.isPending ? <CircularProgress size={14} color="inherit" /> : <AutorenewOutlinedIcon sx={{ fontSize: 14 }} />
                }
                onClick={() => setConfirmRollSecret(true)}
                disabled={rollSigningSecret.isPending}
                sx={{
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 700,
                  height: 30,
                  borderRadius: "8px",
                  borderColor: "rgba(16, 185, 129, 0.5)",
                  color: "#065f46",
                  "&:hover": { borderColor: "#059669", bgcolor: "rgba(16, 185, 129, 0.1)" },
                }}
              >
                Roll Secret
              </Button>
            </Box>
          </Paper>
        ) : null}

        {/* API Key Banner (If exists or can be revealed) */}
        {apiKey ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "16px",
              bgcolor: "var(--fx-colors-yellow-50, #FFFDEB)",
              border: "1px solid #FCD34D",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberOutlinedIcon sx={{ color: "#D97706", fontSize: 18 }} />
              <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#92400E" }}>API Key</Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: "#B45309", lineHeight: 1.4 }}>
              Use as <strong>x-api-key</strong> header for log polling.
            </Typography>
            <TextField
              value={apiKey}
              type={showApiKey ? "text" : "password"}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", bgcolor: "var(--fx-surface-base, #FFFFFF)", borderRadius: "8px", fontSize: 11 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowApiKey((c) => !c)} size="small">
                      {showApiKey ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
                    </IconButton>
                    <IconButton onClick={() => handleCopy(apiKey, "API Key")} size="small">
                      <ContentCopyOutlined fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        ) : isRegistered ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "16px",
              bgcolor: "rgba(251, 142, 0, 0.05)",
              border: "1px solid rgba(251, 142, 0, 0.25)",
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <KeyOutlinedIcon sx={{ color: colors.orange.base, fontSize: 18 }} />
              <Typography sx={{ fontWeight: 800, fontSize: 13, color: colors.gray.base }}>Event Delivery API Key</Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: "var(--fx-surface-textsecondary, #64748B)" }}>
              Required as <strong>x-api-key</strong> header when polling event delivery logs.
            </Typography>
            <Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={revealApiKey.isPending ? <CircularProgress size={14} color="inherit" /> : <VisibilityOutlined fontSize="small" />}
                onClick={handleRevealApiKey}
                disabled={revealApiKey.isPending}
                sx={{
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 700,
                  height: 30,
                  borderRadius: "8px",
                  borderColor: colors.orange.base,
                  color: colors.orange.base,
                  "&:hover": { borderColor: colors.orange[600], bgcolor: "rgba(251, 142, 0, 0.08)" },
                }}
              >
                Reveal API Key
              </Button>
            </Box>
          </Paper>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "1px solid rgba(239, 68, 68, 0.18)",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>Remove webhook settings</Typography>
          <Typography sx={{ fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 500, mt: 0.5, mb: 1.5, lineHeight: 1.45 }}>
            Clears the endpoint and auth so you can start from a clean setup. Delivery logs are kept.
          </Typography>
          <Button
            fullWidth
            startIcon={isRemoving ? undefined : <DeleteOutlineOutlinedIcon />}
            onClick={() => setConfirmRemove(true)}
            disabled={isSaving || isRemoving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              height: 38,
              borderRadius: "10px",
              color: "#b91c1c",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              bgcolor: "rgba(254, 242, 242, 0.8)",
              "&:hover": {
                bgcolor: "rgba(254, 226, 226, 1)",
                borderColor: "#ef4444",
              },
            }}
          >
            {isRemoving ? <CircularProgress size={18} color="inherit" /> : "Remove webhook settings"}
          </Button>
        </Paper>

        <ConfirmDialog
          open={confirmRemove}
          title="Remove webhook settings"
          description="This removes the webhook URL, signing secret, and API key for this company. You can register a new endpoint afterwards. Delivery logs are not deleted."
          confirmText="Remove"
          onConfirm={handleRemove}
          onCancel={() => setConfirmRemove(false)}
          loading={isRemoving}
        />

        <ConfirmDialog
          open={confirmRollSecret}
          title="Roll Webhook Signing Secret?"
          description="Rolling will generate a new signing secret immediately. Your previous secret will remain valid for 24 hours so you can update your webhook receiver without downtime."
          confirmText="Roll Secret"
          onConfirm={handleRollSecret}
          onCancel={() => setConfirmRollSecret(false)}
          loading={rollSigningSecret.isPending}
        />
      </Box>
    );
  }

  // FULL MODE (Unregistered Setup View)
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "20px",
          background: `linear-gradient(135deg, ${colors.orange[50]} 0%, var(--fx-surface-base, #FFFFFF) 100%)`,
          border: `1px solid ${colors.orange[100]}`,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "16px",
              background: `linear-gradient(135deg, ${colors.orange.base}, ${colors.orange[600]})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              boxShadow: "0 6px 16px rgba(251, 142, 0, 0.25)",
            }}
          >
            <NotificationsActiveOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", lineHeight: 1.2 }}>Event Delivery</Typography>
            <Typography sx={{ mt: 0.5, color: "var(--fx-surface-textsecondary, #64748B)", fontSize: 13, fontWeight: 500 }}>
              Configure webhooks to stream live job, visit, and invoice updates to your external systems.
            </Typography>
          </Box>
        </Box>

        <Chip
          size="small"
          label={isRegistered ? (isEnabled ? "Active" : "Paused") : "Unregistered"}
          color={isRegistered ? (isEnabled ? "success" : "warning") : "default"}
          sx={{
            fontWeight: 700,
            fontSize: 12,
            px: 1,
            height: 28,
            borderRadius: "999px",
          }}
        />
      </Paper>

      {/* Section 1: Endpoint & Security */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "20px",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.02)",
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LinkOutlinedIcon sx={{ color: colors.orange.base, fontSize: 20 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>Endpoint & Security</Typography>
        </Box>

        <Divider sx={{ borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)", mb: 0.5 }} />

        {/* Webhook URL Input */}
        <TextField
          label="Webhook URL"
          placeholder="https://api.yourcompany.com/webhooks/flownect"
          value={webhookUrl}
          onChange={(event) => setWebhookUrl(event.target.value)}
          fullWidth
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkOutlinedIcon sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
              </InputAdornment>
            ),
            endAdornment: webhookUrl ? (
              <InputAdornment position="end">
                <Tooltip title="Copy URL">
                  <IconButton size="small" onClick={() => handleCopy(webhookUrl, "Webhook URL")}>
                    <ContentCopyOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
            sx: { borderRadius: "12px", bgcolor: "var(--fx-surface-sunken, #F8FAFC)" },
          }}
          helperText="Must be a valid HTTPS URL capable of receiving POST requests."
        />

        {/* Auth Token (Only for registered / update mode) or HMAC Info for creation */}
        {isRegistered ? (
          <TextField
            label="Auth Token"
            placeholder={config?.hasAuthToken ? "Leave blank to keep existing token" : "Optional secret token"}
            type={showAuthToken ? "text" : "password"}
            value={authToken}
            onChange={(event) => setAuthToken(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyOutlinedIcon sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowAuthToken((current) => !current)} edge="end">
                    {showAuthToken ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                  </IconButton>
                </InputAdornment>
              ),
              sx: { borderRadius: "12px", bgcolor: "var(--fx-surface-sunken, #F8FAFC)" },
            }}
            helperText={
              config?.hasAuthToken
                ? "An Auth Token is currently active. Enter a new string only to replace it."
                : "Optional. Passed as 'Authorization: Bearer <token>' in webhook HTTP headers."
            }
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 2,
              borderRadius: "12px",
              bgcolor: "rgba(16, 185, 129, 0.05)",
              border: "1px solid rgba(16, 185, 129, 0.2)",
            }}
          >
            <SecurityOutlinedIcon sx={{ color: "#059669", fontSize: 20, mt: 0.25 }} />
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#065f46" }}>HMAC-SHA256 Webhook Signatures</Typography>
              <Typography sx={{ fontSize: 12, color: "#047857", mt: 0.25, lineHeight: 1.5 }}>
                When registered, FlowNect generates a secure signing secret. Webhook dispatches are signed with HMAC-SHA256 headers so your server can
                verify authenticity.
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Section 2: Delivery Control */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: isEnabled ? "rgba(34, 197, 94, 0.03)" : "rgba(245, 158, 11, 0.03)",
          border: isEnabled ? "1px solid rgba(34, 197, 94, 0.2)" : "1px solid rgba(245, 158, 11, 0.2)",
          transition: "all 0.3s ease",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={isEnabled}
              onChange={(event) => handleToggleEnable(event.target.checked)}
              color="warning"
              sx={{ ml: 0.5 }}
              disabled={isSaving}
            />
          }
          label={
            <Box sx={{ ml: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 15, color: colors.gray.base }}>
                {isEnabled ? "Notifications Enabled" : "Notifications Paused"}
              </Typography>
              <Typography sx={{ color: colors.gray[600] || "var(--fx-surface-textsecondary, #64748B)", fontSize: 13, fontWeight: 500, mt: 0.25 }}>
                {isEnabled
                  ? "Event dispatches are active. FlowNect will post payload events to your endpoint instantly."
                  : "Delivery is paused. Event payloads will not be sent until re-enabled."}
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
        />
      </Paper>

      {/* Section 3: Secrets Banners */}
      {/* Signing Secret Card (If exists or can be revealed) */}
      {signingSecret ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "rgba(16, 185, 129, 0.06)",
            border: "1px solid rgba(16, 185, 129, 0.3)",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SecurityOutlinedIcon sx={{ color: "#059669" }} />
              <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#065f46" }}>Webhook Signing Secret</Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                rollSigningSecret.isPending ? <CircularProgress size={14} color="inherit" /> : <AutorenewOutlinedIcon sx={{ fontSize: 15 }} />
              }
              onClick={() => setConfirmRollSecret(true)}
              disabled={rollSigningSecret.isPending}
              sx={{
                textTransform: "none",
                fontSize: 12,
                fontWeight: 700,
                height: 32,
                px: 1.5,
                borderRadius: "10px",
                borderColor: "rgba(16, 185, 129, 0.4)",
                color: "#065f46",
                "&:hover": { borderColor: "#059669", bgcolor: "rgba(16, 185, 129, 0.1)" },
              }}
            >
              Roll Secret
            </Button>
          </Box>

          {previousSecretValidUntil ? (
            <Typography sx={{ fontSize: 13, color: "#047857", fontWeight: 600 }}>
              Previous secret remains valid until {new Date(previousSecretValidUntil).toLocaleString()} for zero-downtime transition.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 13, color: "#047857", lineHeight: 1.5 }}>
              Use this signing secret to verify that webhook dispatches come from FlowNect via HMAC-SHA256 signatures.
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <TextField
              value={signingSecret}
              type={showSigningSecret ? "text" : "password"}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: {
                  fontFamily: "monospace",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  borderRadius: "10px",
                  fontWeight: 600,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showSigningSecret ? "Hide" : "Show"}>
                      <IconButton onClick={() => setShowSigningSecret((current) => !current)} edge="end" size="small">
                        {showSigningSecret ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy Signing Secret">
                      <IconButton onClick={() => handleCopy(signingSecret, "Signing Secret")} edge="end" size="small" sx={{ ml: 0.5 }}>
                        <ContentCopyOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>
      ) : isRegistered && config?.hasSigningSecret ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "rgba(16, 185, 129, 0.05)",
            border: "1px solid rgba(16, 185, 129, 0.25)",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SecurityOutlinedIcon sx={{ color: "#059669" }} />
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#065f46" }}>Webhook Signing Secret</Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: "#047857", lineHeight: 1.5 }}>
            Webhook dispatches are signed with HMAC-SHA256. Reveal your signing secret or rotate it if needed.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, pt: 0.5 }}>
            <Button
              variant="outlined"
              startIcon={revealSigningSecret.isPending ? <CircularProgress size={16} color="inherit" /> : <VisibilityOutlined fontSize="small" />}
              onClick={handleRevealSigningSecret}
              disabled={revealSigningSecret.isPending}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                height: 36,
                px: 2,
                borderRadius: "10px",
                borderColor: "rgba(16, 185, 129, 0.5)",
                color: "#065f46",
                "&:hover": { borderColor: "#059669", bgcolor: "rgba(16, 185, 129, 0.1)" },
              }}
            >
              Reveal Signing Secret
            </Button>
            <Button
              variant="outlined"
              startIcon={rollSigningSecret.isPending ? <CircularProgress size={16} color="inherit" /> : <AutorenewOutlinedIcon fontSize="small" />}
              onClick={() => setConfirmRollSecret(true)}
              disabled={rollSigningSecret.isPending}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                height: 36,
                px: 2,
                borderRadius: "10px",
                borderColor: "rgba(16, 185, 129, 0.5)",
                color: "#065f46",
                "&:hover": { borderColor: "#059669", bgcolor: "rgba(16, 185, 129, 0.1)" },
              }}
            >
              Roll Secret
            </Button>
          </Box>
        </Paper>
      ) : null}

      {/* API Key Card (If exists or can be revealed) */}
      {apiKey ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "var(--fx-colors-yellow-50, #FFFDEB)",
            border: "1px solid #FCD34D",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberOutlinedIcon sx={{ color: "#D97706" }} />
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#92400E" }}>Event Delivery API Key</Typography>
          </Box>

          <Typography sx={{ fontSize: 13, color: "#B45309", lineHeight: 1.5 }}>
            Use this key as the <strong>x-api-key</strong> HTTP header when making calls to the event delivery API (e.g. polling logs).
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <TextField
              value={apiKey}
              type={showApiKey ? "text" : "password"}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: {
                  fontFamily: "monospace",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  borderRadius: "10px",
                  fontWeight: 600,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showApiKey ? "Hide" : "Show"}>
                      <IconButton onClick={() => setShowApiKey((current) => !current)} edge="end" size="small">
                        {showApiKey ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy API Key">
                      <IconButton onClick={() => handleCopy(apiKey, "API Key")} edge="end" size="small" sx={{ ml: 0.5 }}>
                        <ContentCopyOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>
      ) : isRegistered ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "rgba(251, 142, 0, 0.05)",
            border: "1px solid rgba(251, 142, 0, 0.25)",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <KeyOutlinedIcon sx={{ color: colors.orange.base, fontSize: 20 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: colors.gray.base }}>Event Delivery API Key</Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: "var(--fx-surface-textsecondary, #64748B)", lineHeight: 1.5 }}>
            Required as the <strong>x-api-key</strong> header when interacting with polling endpoints.
          </Typography>
          <Box sx={{ pt: 0.5 }}>
            <Button
              variant="outlined"
              startIcon={revealApiKey.isPending ? <CircularProgress size={16} color="inherit" /> : <VisibilityOutlined fontSize="small" />}
              onClick={handleRevealApiKey}
              disabled={revealApiKey.isPending}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                height: 36,
                px: 2,
                borderRadius: "10px",
                borderColor: colors.orange.base,
                color: colors.orange.base,
                "&:hover": { borderColor: colors.orange[600], bgcolor: "rgba(251, 142, 0, 0.08)" },
              }}
            >
              Reveal API Key
            </Button>
          </Box>
        </Paper>
      ) : null}

      {/* Action Footer */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1 }}>
        <Button
          sx={{
            ...brandButton,
            minWidth: "160px",
            height: "44px",
          }}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <>
              <SaveOutlinedIcon sx={{ fontSize: 18 }} />
              {isRegistered ? "Save Changes" : "Register Webhook"}
            </>
          )}
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmRollSecret}
        title="Roll Webhook Signing Secret?"
        description="Rolling will generate a new signing secret immediately. Your previous secret will remain valid for 24 hours so you can update your webhook receiver without downtime."
        confirmText="Roll Secret"
        onConfirm={handleRollSecret}
        onCancel={() => setConfirmRollSecret(false)}
        loading={rollSigningSecret.isPending}
      />
    </Box>
  );
};
